import React from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { formatExchangeDisplay } from "./formatExchangeDisplay";
import type {
  AlertHistoryVariant,
  SharedAlertHistoryItem,
} from "./alert-history.types";

interface AlertHistoryProps {
  alerts: SharedAlertHistoryItem[];
  variant?: AlertHistoryVariant;
  description?: string;
  baseSymbol?: string;
}

function getPriceChangeVsPrevious(
  currentPrice: number,
  previousAlert: SharedAlertHistoryItem | undefined
): number | null {
  if (!previousAlert?.price || previousAlert.price === 0) return null;
  return ((currentPrice - previousAlert.price) / previousAlert.price) * 100;
}

function getTierBadgeColor(tier: number | null): string {
  if (tier === null) return "bg-gray-500/20 text-gray-400";
  if (tier === 1) return "bg-yellow-500/20 text-yellow-400";
  if (tier === 2) return "bg-blue-500/20 text-blue-400";
  return "bg-gray-500/20 text-gray-400";
}

function formatDateTime(date: Date): string {
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h24",
  });
}

export const AlertHistory: React.FC<AlertHistoryProps> = ({
  alerts,
  variant = "drawer",
  description,
  baseSymbol,
}) => {
  if (alerts.length === 0) return null;

  const isDrawer = variant.startsWith("drawer");
  const isPageDesktop = variant === "page-desktop";
  const isPageMobile = variant === "page-mobile";
  const isPage = isPageDesktop || isPageMobile;

  const containerClass = isDrawer
    ? "space-y-3 bg-primary/40 border border-gray-500/30 rounded-lg p-3"
    : isPageDesktop
      ? "bg-secondary border border-border rounded-xl p-6 space-y-4"
      : "bg-secondary border border-border rounded-xl p-4";

  const itemClass = isDrawer
    ? "flex items-center justify-between bg-primary/60 rounded-md p-2"
    : isPageDesktop
      ? "flex items-center justify-between bg-tertiary border border-border rounded-lg p-4 hover:bg-tertiary/80 transition-colors"
      : "flex justify-between items-center py-2 border-b border-border last:border-0";

  const titleClass = isPageDesktop
    ? "text-lg font-bold text-text-primary"
    : "text-sm font-bold text-text-primary";

  const getExchangeLabel = (
    exchange: string,
    exchangeType: string | null
  ) => {
    if (isPage) {
      return formatExchangeDisplay(exchange, exchangeType);
    }
    return exchange;
  };

  return (
    <div className={isPage ? "space-y-4" : "space-y-3"}>
      <div className={containerClass}>
        <h3 className={titleClass}>Alert History</h3>
        {description ? (
          <p
            className={
              isDrawer
                ? "text-xs text-text-secondary mb-2"
                : "text-sm text-text-secondary"
            }
          >
            {description}
          </p>
        ) : null}
        {isPage && baseSymbol ? (
          <p className="text-sm text-text-secondary">
            Historical listing and delisting events for {baseSymbol}
          </p>
        ) : null}
        <div className={isPage ? "space-y-3" : "space-y-2"}>
          {alerts.map((alert, index) => {
            const alertDate = new Date(alert.createdAt);
            const isListingAlert = alert.type === "new_coin";
            const previousAlert = alerts[index + 1];
            const priceChangeVsPrev =
              alert.price !== null
                ? getPriceChangeVsPrevious(alert.price, previousAlert)
                : null;
            const tier = alert.tier ?? null;
            const rowKey =
              alert.id ?? `${alert.exchange}-${String(alert.createdAt)}-${index}`;

            return (
              <div key={rowKey} className={itemClass}>
                <div className="flex-1">
                  <div
                    className={`flex items-center gap-2 flex-wrap ${isPage ? "mb-2" : ""}`}
                  >
                    {isListingAlert ? (
                      <TrendingUp
                        className={
                          isPageDesktop
                            ? "w-5 h-5 text-green-400"
                            : "w-4 h-4 text-green-400"
                        }
                      />
                    ) : (
                      <TrendingDown
                        className={
                          isPageDesktop
                            ? "w-5 h-5 text-red-400"
                            : "w-4 h-4 text-red-400"
                        }
                      />
                    )}
                    <span
                      className={
                        isPageDesktop
                          ? "text-base text-accent font-semibold"
                          : "text-sm text-accent font-medium capitalize"
                      }
                    >
                      {getExchangeLabel(alert.exchange, alert.exchangeType)}
                    </span>
                    {variant === "drawer" ? (
                      <span className="text-xs text-text-secondary">
                        {alert.exchangeType || "spot"}
                      </span>
                    ) : null}
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs ${
                        isListingAlert
                          ? "bg-green-500/20 text-green-400"
                          : "bg-red-500/20 text-red-400"
                      } ${isPage ? "px-2.5 py-1 text-sm font-medium" : ""}`}
                    >
                      {isListingAlert ? "Listed" : "Delisted"}
                    </span>
                    {typeof alert.tier === "number" &&
                    variant !== "page-mobile" ? (
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${getTierBadgeColor(
                          tier
                        )} ${isPage ? "px-2.5 py-1 text-sm" : ""}`}
                      >
                        Tier{"🔥".repeat(Math.max(0, 4 - alert.tier))}
                      </span>
                    ) : null}
                  </div>
                  {variant !== "page-mobile" ? (
                    <p
                      className={
                        isPage
                          ? "text-sm text-text-secondary"
                          : "text-xs text-text-secondary mt-0.5"
                      }
                    >
                      {variant === "drawer-desktop"
                        ? alertDate.toLocaleDateString()
                        : formatDateTime(alertDate)}
                    </p>
                  ) : null}
                </div>
                <div className="text-right">
                  {alert.price !== null ? (
                    <>
                      <p
                        className={
                          isPage
                            ? "text-base font-medium text-text-primary"
                            : "text-sm text-text-primary"
                        }
                      >
                        $
                        {alert.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: isPage ? 6 : 4,
                        })}
                      </p>
                      {priceChangeVsPrev !== null ? (
                        <p
                          className={`text-xs ${
                            priceChangeVsPrev >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {priceChangeVsPrev >= 0 ? "+" : ""}
                          {priceChangeVsPrev.toFixed(2)}% vs previous
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p
                      className={
                        isPage
                          ? "text-sm text-text-secondary"
                          : "text-xs text-text-secondary"
                      }
                    >
                      {isPage ? "Price N/A" : "Not available"}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
